import React, { useEffect, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/formatters';

interface DashboardData {
  totalLeads: number;
  inNegotiation: number;
  dealsClosed: number;
  earnings: number;
  stageCounts: { status: string; count: number }[];
}

const PartnerDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);

  const fetchData = async () => {
    const response = await axios.get('/api/partner/dashboard');
    setData(response.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = [
    { label: t('dashboard.totalLeads'), value: data?.totalLeads ?? 0 },
    { label: t('dashboard.inNegotiation'), value: data?.inNegotiation ?? 0 },
    { label: t('dashboard.dealsClosed'), value: data?.dealsClosed ?? 0 },
    { label: t('dashboard.earnings'), value: formatCurrency(data?.earnings ?? 0, i18n.resolvedLanguage) },
  ];

  const wonCount = data?.stageCounts?.find((entry) => entry.status === 'CLOSED_WON')?.count ?? 0;
  const activeCount = (data?.stageCounts || [])
    .filter((entry) => ['QUALIFIED', 'OUTREACH_STARTED', 'MEETING_SCHEDULED', 'PROPOSAL_SENT', 'IN_NEGOTIATION'].includes(entry.status))
    .reduce((sum, entry) => sum + entry.count, 0);
  const progressPercent = activeCount + wonCount > 0 ? Math.round((wonCount / (activeCount + wonCount)) * 100) : 0;

  return (
    <section>
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('dashboard.partnerTitle')}</h1>
      <p className="max-w-3xl text-sm text-[#111D4E]/80 mb-6">{t('dashboard.partnerIntro')}</p>
      <div className="flex justify-end mb-4">
        <button onClick={fetchData} className="px-3 py-1 bg-[#47ABDB] text-white rounded text-sm">{t('common.refresh')}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
              <p className="text-xs font-semibold text-[#111D4E] uppercase tracking-wide">{stat.label}</p>
            </div>
            <div className="px-5 py-5">
              <p className="text-3xl font-semibold text-[#111D4E]">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
            <p className="text-sm font-semibold text-[#111D4E]">{t('dashboard.progressTitle')}</p>
            <p className="text-xs text-gray-600">{t('dashboard.progressSubtitle')}</p>
          </div>
          <div className="px-5 py-5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-600">{t('dashboard.confirmedShare')}</p>
              <p className="text-2xl font-semibold text-[#111D4E]">{progressPercent}%</p>
            </div>
            <div className="mt-3 h-3 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full rounded-full bg-[#47ABDB]" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
              {(data?.stageCounts || []).map((entry) => (
                <div key={entry.status} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t(`partnerProgress.steps.${entry.status}`, t(`status.deal.${entry.status}`))}</div>
                  <div className="mt-2 text-2xl font-semibold text-[#111D4E]">{entry.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerDashboard;
